import {ChangeDetectionStrategy, Component, forwardRef, Input,} from '@angular/core';

import {UntilDestroy} from '@ngneat/until-destroy';
import {AcTableComponent} from '../../ac-table.component';
import {AcTableRow, AcTableSorters, SorterFunc} from '../../models/ac-table.interface';
import {AcPagingEvent} from '../../../ac-pagination/ac-paging.interface';
import {StringUtils} from '../../../../utils/string-utils';

@UntilDestroy()
@Component({
    selector: 'ac-table-client',
    templateUrl: '../../ac-table.component.html',
    styleUrls: ['../../ac-table.component.less'],
    providers: [{provide: AcTableComponent, useExisting: forwardRef(() => AcTableClientComponent)}],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AcTableClientComponent extends AcTableComponent {

    _allRows: AcTableRow[];
    viewInitialized = false;

    @Input() sorters: AcTableSorters;

    ngOnInit() {
        this.sorters = {
            number: this.numberCompare,
            string: this.stringCompare,
            boolean: this.booleanCompare,
            version: this.versionCompare,
            ...this.sorters
        };
    }

    ngAfterViewInit() {
        super.ngAfterViewInit();
        this.viewInitialized = true;
        this._allRows && this.setRows([...this._allRows]);
    }

    onPageChange(paging?: AcPagingEvent) {
        super.onPageChange(paging);
        this.setRows();
    }

    onColumnSort(column: any) {
        super.onColumnSort(column);
        this.setRows();
    }

    setRows(rows?: AcTableRow[]) {
        if (rows) {
            this._allRows = rows;
            this.totalElements = this._allRows?.length || 0;
            this.loading = true;
        }

        if (!this.viewInitialized) {
            return;
        }

        let sortedRows: AcTableRow[] = this.getSortedRows();
        if (this.paginator && this.pageSelector) {
            const pagesCount = Math.ceil(sortedRows.length / this.pageSize);
            if (this.pageIndex > pagesCount && pagesCount !== 0) {
                this.onPageChange({page: 1})
            }
            sortedRows = sortedRows.slice((this.pageIndex - 1) * this.pageSize, this.pageIndex * this.pageSize);
        }

        super.setRows(sortedRows);
    }

    private getSortedRows(): AcTableRow[] {
        if (this._sorting?.length <= 0 || !this._allRows) {
            return this._allRows || [];
        }

        const sortedRows: AcTableRow[] = [...this._allRows];
        sortedRows.length > 0 && this._sorting.forEach((sorterState) => {
            const sorterType = typeof (StringUtils.byString(sortedRows[0].data, sorterState.field));
            const sorter: SorterFunc = this.sorters[sorterState.sorter || sorterType] || this.sorters.string;

            sorter && sortedRows.sort(({data: row1}, {data: row2}) => {
                return sorter(StringUtils.byString(row1, sorterState.field), StringUtils.byString(row2, sorterState.field), sorterState.dir);
            });
        });
        return sortedRows;
    }

    private numberCompare: SorterFunc = (exp1, exp2, dir) => {
        let compareResult;
        if (exp2 < 0) {
            compareResult = -1;
        } else if (parseFloat(exp1 || 0) < 0) {
            compareResult = 1;
        } else {
            compareResult = parseFloat(exp1|| 0) - parseFloat(exp2|| 0);
        }

        return dir === 'asc' ? compareResult : (compareResult * -1);
    };

    private stringCompare: SorterFunc = (string1 = '', string2 = '', dir) => {
        const compareResult = ((string1 || '').toLowerCase()).localeCompare((string2 || '').toLowerCase());
        return dir === 'asc' ? compareResult : (compareResult * -1);
    };

    private booleanCompare: SorterFunc = (exp1, exp2, dir) => {
        const el1 = (exp1 === true || exp1 === 'true' || exp1 === 'True' || exp1 === 1) ? 1 : 0;
        const el2 = exp2 === true || exp2 === 'true' || exp2 === 'True' || exp2 === 1 ? 1 : 0;

        return dir === 'asc' ? el1 - el2 : el2 - el1;
    };

    private versionCompare: SorterFunc = (ver1, ver2, dir) => {
        const isBigger = this.isGt(ver1, ver2);
        return ((dir === 'asc' && isBigger) || (dir === 'desc' && !isBigger)) ? 1 : -1;
    };

    private isGt = (ver1, ver2) => {
        if (!ver1 || !ver2) {
            return false;
        }

        const parts1 = ver1.split('.');
        parts1.length = 3;
        const parts2 = ver2.split('.');

        for (let i = 0; i < parts1.length; i++) {
            const p1 = (parseInt(parts1[i], 10) || 0);
            const p2 = (parseInt(parts2[i], 10) || 0);

            if (p1 < p2) {
                return false;
            } else if (p1 > p2){
                return true;
            }
        }
    };
}
